import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ChevronRight, ChevronLeft, Check, Building2, MapPin, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { invalidateLandCache } from "../hooks/useLand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VAT_PREVIEW = {
  DE: { speisen: "7%", getraenke: "19%", label: "Deutschland" },
  AT: { speisen: "10%", getraenke: "20%", label: "Österreich" },
};

/**
 * OnboardingWizard
 * Erscheint automatisch nach Login, wenn `unternehmenseinstellungen.firmenname`
 * noch nicht gesetzt ist. Speichert Land + Firmenname + Stadt in 3 Schritten.
 * Nutzer kann auch "Spaeter" wählen (wird via localStorage 24h unterdrückt).
 */
export default function OnboardingWizard({ user }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    land: "DE",
    firmenname: "",
    stadt: "",
  });

  useEffect(() => {
    if (!user) return;
    // Demo-User skippen
    if (user.user_id?.startsWith("demo-")) return;
    // Snooze-Check
    const snoozedUntil = parseInt(localStorage.getItem("margevio_onboarding_snoozed_until") || "0", 10);
    if (snoozedUntil > Date.now()) return;

    // Backend-State pruefen
    (async () => {
      try {
        const res = await axios.get(`${API}/unternehmenseinstellungen`, { withCredentials: true });
        const firmenname = res.data?.firmenname?.trim();
        if (!firmenname) {
          setForm((f) => ({
            ...f,
            land: res.data?.land === "AT" ? "AT" : "DE",
            firmenname: "",
            stadt: res.data?.stadt || "",
          }));
          setOpen(true);
        }
      } catch {
        /* still allow opening if endpoint not reachable */
      }
    })();
  }, [user]);

  const snooze = () => {
    localStorage.setItem("margevio_onboarding_snoozed_until", String(Date.now() + 24 * 60 * 60 * 1000));
    setOpen(false);
  };

  const next = () => {
    if (step === 1 && !form.firmenname.trim()) {
      toast.error("Bitte Firmenname eingeben");
      return;
    }
    setStep((s) => Math.min(s + 1, 2));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/unternehmenseinstellungen`,
        {
          firmenname: form.firmenname.trim(),
          stadt: form.stadt.trim() || null,
          land: form.land,
        },
        { withCredentials: true }
      );
      invalidateLandCache();
      toast.success("Willkommen bei MargeVio!");
      localStorage.removeItem("margevio_onboarding_snoozed_until");
      setOpen(false);
      // Reload damit useLand & alle abhaengigen Hooks frisch laden
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const vat = VAT_PREVIEW[form.land];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && snooze()}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white p-0 overflow-hidden"
        data-testid="onboarding-wizard"
      >
        <VisuallyHidden>
          <DialogTitle>Willkommen bei MargeVio – Onboarding</DialogTitle>
          <DialogDescription>Drei Schritte: Land, Firmenname, Bestätigung</DialogDescription>
        </VisuallyHidden>
        {/* Header */}
        <div className="bg-gradient-to-br from-violet-600/30 via-slate-900 to-slate-900 px-6 pt-6 pb-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-violet-500/20 rounded-lg">
              <Sparkles size={20} className="text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Willkommen bei MargeVio</h2>
              <p className="text-xs text-slate-400">Drei kurze Schritte – dann kann's losgehen.</p>
            </div>
          </div>
          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === step ? "w-8 bg-violet-400" : i < step ? "w-4 bg-violet-600" : "w-4 bg-slate-700"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 min-h-[260px]">
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-land">
              <div>
                <h3 className="text-base font-medium mb-1">In welchem Land bist du tätig?</h3>
                <p className="text-xs text-slate-400">Bestimmt MwSt./USt., Lohnsteuer und Feiertage.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {["DE", "AT"].map((c) => {
                  const active = form.land === c;
                  return (
                    <button
                      key={c}
                      onClick={() => setForm((f) => ({ ...f, land: c }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        active
                          ? "border-violet-500 bg-violet-500/10"
                          : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                      }`}
                      data-testid={`onboarding-land-${c.toLowerCase()}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xl">{c === "DE" ? "🇩🇪" : "🇦🇹"}</span>
                        {active && <Check size={16} className="text-violet-400" />}
                      </div>
                      <div className="text-sm font-medium">{c === "DE" ? "Deutschland" : "Österreich"}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {c === "DE" ? "7 / 19 % MwSt." : "10 / 20 % USt."}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-firma">
              <div>
                <h3 className="text-base font-medium mb-1">Wie heißt dein Betrieb?</h3>
                <p className="text-xs text-slate-400">Erscheint auf Berichten, Speisekarten und Rechnungen.</p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="ob-firma" className="text-xs text-slate-400">
                    Firmenname *
                  </Label>
                  <Input
                    id="ob-firma"
                    value={form.firmenname}
                    onChange={(e) => setForm((f) => ({ ...f, firmenname: e.target.value }))}
                    placeholder="z.B. Café Sonnenschein"
                    className="bg-slate-800 border-slate-700 mt-1"
                    autoFocus
                    data-testid="onboarding-firmenname-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ob-stadt" className="text-xs text-slate-400">
                    Stadt (optional)
                  </Label>
                  <Input
                    id="ob-stadt"
                    value={form.stadt}
                    onChange={(e) => setForm((f) => ({ ...f, stadt: e.target.value }))}
                    placeholder={form.land === "AT" ? "z.B. Wien" : "z.B. Berlin"}
                    className="bg-slate-800 border-slate-700 mt-1"
                    data-testid="onboarding-stadt-input"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-confirm">
              <div>
                <h3 className="text-base font-medium mb-1">Passt alles?</h3>
                <p className="text-xs text-slate-400">Du kannst alles später unter Einstellungen ändern.</p>
              </div>
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-slate-700 rounded">
                    <Building2 size={14} className="text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-slate-400">Firmenname</div>
                    <div className="text-sm font-medium truncate">{form.firmenname || "—"}</div>
                  </div>
                </div>
                {form.stadt && (
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-slate-700 rounded">
                      <MapPin size={14} className="text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-slate-400">Stadt</div>
                      <div className="text-sm font-medium truncate">{form.stadt}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-slate-700 rounded text-base">
                    {form.land === "DE" ? "🇩🇪" : "🇦🇹"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-slate-400">Land · Steuersätze</div>
                    <div className="text-sm font-medium">
                      {vat.label} · Speisen {vat.speisen} · Getränke {vat.getraenke}
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                Hauptstandort wird automatisch angelegt. Weitere Standorte kannst du später unter
                Einstellungen → Standorte hinzufügen.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between bg-slate-900/60">
          <button
            onClick={snooze}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="onboarding-snooze-btn"
          >
            Später erinnern
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={prev}
                disabled={saving}
                className="border-slate-700"
                data-testid="onboarding-prev-btn"
              >
                <ChevronLeft size={14} /> Zurück
              </Button>
            )}
            {step < 2 ? (
              <Button
                size="sm"
                onClick={next}
                className="bg-violet-600 hover:bg-violet-500"
                data-testid="onboarding-next-btn"
              >
                Weiter <ChevronRight size={14} />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={finish}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-500"
                data-testid="onboarding-finish-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Fertig
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
